import {
  collection, doc, onSnapshot, query, serverTimestamp, setDoc, where,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app, db } from './config';
import { FUNCTIONS_REGION } from './region';
import { dayKey, sessionId } from '../session/sessionKey';
import type { SessionMode } from '../session/turns';
import { teachingLanguages, type LanguageCode } from '../i18n/languages';

export type OpenSession = {
  id: string;
  muqriId: string;
  muqriName: string;
  mode: SessionMode;
  waitingCount: number;
  /** Millis of the معلم's last beat; absent on sessions opened before this. */
  muqriSeenAt?: number;
  /** Whether a call is running, which counts as attended on its own. */
  hasActiveCall?: boolean;
  /**
   * The languages this معلم teaches in — م-7-02.
   *
   * Carried on the SESSION rather than read from the teacher's own document,
   * because a pupil may not read another user's document at all: the rules
   * allow a user, an admin, a parent and a class teacher, and a pupil
   * browsing the list is none of those. Copied from the teacher's profile at
   * the moment they open, which is also when it is true.
   *
   * Empty for every session opened before المرحلة 7, which the filter reads
   * as "not said" rather than as "no languages".
   */
  languages: LanguageCode[];
  /**
   * The معلم's gender — sessions are same-gender, and this is what lets a
   * pupil's list show only the sessions they could actually be admitted to.
   *
   * Null on sessions opened before the rule, and on a teacher who never
   * recorded one. Null means NO here, not "unknown, allow": the callable
   * refuses such a pair anyway, and listing it would send a pupil to queue
   * for a room they cannot enter.
   */
  muqriGender: 'male' | 'female' | null;
};

/** The server-owned half of a session document, as the clients read it. */
export type ActiveCall = { room: string; participants: string[] };

export type LiveSession = {
  id: string;
  muqriId: string;
  muqriName: string;
  mode: SessionMode;
  status: 'open' | 'closed';
  activeCall: ActiveCall | null;
  currentReciterId: string | null;
};

function toLiveSession(id: string, data: Record<string, unknown>): LiveSession {
  const call = data.activeCall as { room?: unknown; participants?: unknown } | undefined;
  return {
    id,
    muqriId: String(data.muqriId ?? ''),
    muqriName: String(data.muqriName ?? ''),
    mode: (data.mode === 'halaqa' ? 'halaqa' : 'private') as SessionMode,
    status: data.status === 'open' ? 'open' : 'closed',
    activeCall: call && typeof call.room === 'string' && call.room
      ? { room: call.room, participants: Array.isArray(call.participants) ? call.participants.map(String) : [] }
      : null,
    currentReciterId: typeof data.currentReciterId === 'string' ? data.currentReciterId : null,
  };
}

/**
 * C3 — the ONE subscription that tells a client what the server decided.
 * activeCall.participants and currentReciterId are server-owned (the rules
 * refuse every client write to them), which is exactly why they have to be
 * READ: a student who has been admitted learns it here and nowhere else, and
 * the معلم's idea of who is in the حلقة must come from here rather than from
 * the waiting queue, which lists people who have not been admitted at all.
 *
 * Without it the waiting room watched only the queue subcollection: the معلم
 * admitted a student, the server wrote the room, and the student's screen went
 * on showing their queue position while the معلم sat alone.
 */
export function listenToSession(
  id: string,
  cb: (session: LiveSession | null) => void,
  onError?: (e: Error) => void
): () => void {
  return onSnapshot(
    doc(db, 'sessions', id),
    (snap) => cb(snap.exists() ? toLiveSession(snap.id, snap.data() as Record<string, unknown>) : null),
    (e) => onError?.(e)
  );
}

/**
 * Says the معلم is still watching this session.
 *
 * serverTimestamp, never a device clock: the rules pin this field to
 * request.time, so a post-dated beat would keep an abandoned session in the
 * discovery list forever — the exact thing presence exists to prevent.
 */
export async function touchSession(sessionIdValue: string): Promise<void> {
  await setDoc(
    doc(db, 'sessions', sessionIdValue),
    { muqriSeenAt: serverTimestamp() },
    { merge: true }
  );
}

export async function openSession(
  muqriId: string,
  muqriName: string,
  mode: SessionMode,
  languages: unknown = [],
  muqriGender: unknown = null,
): Promise<string> {
  const id = sessionId(muqriId, new Date());
  // waitingCount is NOT written here — the rules reject it and the trigger owns it.
  await setDoc(
    doc(db, 'sessions', id),
    {
      muqriId, muqriName, mode, status: 'open', date: dayKey(new Date()),
      // Cleaned here rather than trusted: this is what pupils filter on.
      languages: teachingLanguages(languages),
      // Written onto the session so a pupil can tell, without reading the
      // teacher's own document, which the rules do not allow them.
      muqriGender: muqriGender === 'male' || muqriGender === 'female' ? muqriGender : null,
      openedAt: serverTimestamp(),
      // Listed from the moment it opens, without waiting for the first beat.
      muqriSeenAt: serverTimestamp(),
    },
    { merge: true }
  );
  return id;
}

/**
 * C4 — closing is a CALLABLE, not a write, because what a close has to clear is
 * server-owned. This used to be updateDoc({ status: 'closed' }), and the rules
 * refuse every client write to activeCall, currentReciterId and waitingCount,
 * so the close left all three exactly as the morning had left them. Since a
 * session document is one per معلم per DAY, reopening reuses it and
 * getSessionToken then read the morning's participants and currentReciterId
 * off the stored document — a publish grant for a student nobody had admitted
 * to the afternoon session. The rules now also refuse a client-set
 * status: 'closed', so this is the only way a session closes.
 */
export async function closeSession(
  sessionIdValue: string, position?: { surah: number; ayah: number }
): Promise<void> {
  const call = httpsCallable<
    { sessionId: string; position?: { surah: number; ayah: number } }, { ok: boolean }
  >(getFunctions(app, FUNCTIONS_REGION), 'closeSession');
  // The marker rides along so the server can record where the student who
  // still held the turn got to — otherwise the last student of every session
  // is the one person whose progress is never written. WHOSE record it is is
  // decided server-side, from its own currentReciterId.
  await call({ sessionId: sessionIdValue, position });
}

/**
 * Admission is the muqri adding a student to the room — and it is a CALLABLE,
 * not a write. This used to updateDoc activeCall.room and participants from
 * the device, which meant any signed-in user could author a session document
 * naming somebody else's room and have the token function mint them a
 * join-and-publish token into it. The client never authorises itself: it
 * sends the two ids and the server, reading the STORED session, decides
 * whether the caller is that session's معلم and what the room is called.
 *
 * `muqriId` is kept in the signature for the call site's readability only —
 * it is deliberately NOT sent, because the server must not learn who the
 * owner is from the caller.
 */
export async function admitStudent(
  sessionIdValue: string, studentId: string, _muqriId?: string
): Promise<string> {
  const call = httpsCallable<{ sessionId: string; studentId: string }, { room: string }>(
    getFunctions(app, FUNCTIONS_REGION),
    'admitStudent'
  );
  const res = await call({ sessionId: sessionIdValue, studentId });
  return res.data.room;
}

/**
 * Hands the microphone to the next student — and, like admission, it is a
 * CALLABLE, not a write. currentReciterId is server-owned (firestore.rules
 * refuses any client write to it, for the same reason activeCall is
 * server-owned: the token function trusts it directly to decide who may
 * publish). The caller sends only who should recite next; the server reads
 * the STORED session to check that the caller is that session's معلم and
 * that nextStudentId is actually in the room, and it revokes the outgoing
 * reciter's publish grant at the media server, not only in Firestore.
 */
export async function passTurn(
  sessionIdValue: string,
  nextStudentId: string | null,
  position?: { surah: number; ayah: number }
): Promise<void> {
  const call = httpsCallable<
    { sessionId: string; nextStudentId: string | null; position?: { surah: number; ayah: number } },
    { ok: boolean }
  >(getFunctions(app, FUNCTIONS_REGION), 'passTurn');
  // The turn moving away from someone is them finishing, so the marker goes
  // with it. The server attributes it to its OWN outgoing reciter, never to
  // anything this caller names.
  await call({ sessionId: sessionIdValue, nextStudentId, position });
}

/**
 * Leaving — a CALLABLE, for the same reason admission is.
 *
 * "Leave" used to be navigation and a LiveKit disconnect, and told the server
 * nothing at all: the student stayed in activeCall.participants, so the معلم
 * kept counting them as present, getSessionToken kept authorising them, and
 * the waiting room pulled them straight back into the call on their next
 * visit — because that screen navigates to Call the moment it sees them in
 * participants. They could not leave.
 *
 * Deliberately NOT awaited-then-navigated at the call site: a student pressing
 * leave must get out whether or not the network cooperates, and the call is
 * idempotent, so a retry costs nothing.
 */
export async function leaveSession(sessionIdValue: string): Promise<void> {
  const call = httpsCallable<{ sessionId: string }, { ok: boolean }>(
    getFunctions(app, FUNCTIONS_REGION),
    'leaveSession'
  );
  await call({ sessionId: sessionIdValue });
}

/** One query, no per-session reads: name and count are denormalised onto the doc. */
export function listenToOpenSessions(
  cb: (sessions: OpenSession[]) => void,
  onError?: (e: Error) => void
): () => void {
  const q = query(
    collection(db, 'sessions'),
    where('date', '==', dayKey(new Date())),
    where('status', '==', 'open')
  );
  return onSnapshot(
    q,
    (snap) => cb(snap.docs.map((d) => {
      const data = d.data() as Record<string, unknown>;
      const seen = data.muqriSeenAt as { toMillis?: () => number } | number | undefined;
      return {
        id: d.id,
        ...(data as Omit<OpenSession, 'id'>),
        // A server timestamp arrives as a Timestamp, and as null for the
        // instant between the write being sent and acknowledged.
        muqriSeenAt: typeof seen === 'number'
          ? seen
          : (seen && typeof seen.toMillis === 'function' ? seen.toMillis() : undefined),
        hasActiveCall: !!(data.activeCall as { room?: string } | null)?.room,
        // Cleaned on the way in as well as on the way out: a session written
        // by an older client, or by hand, must not put a language on screen
        // that the app cannot display.
        languages: teachingLanguages(data.languages),
        muqriGender: data.muqriGender === 'male' || data.muqriGender === 'female'
          ? data.muqriGender
          : null,
      };
    })),
    (e) => onError?.(e)
  );
}
